import { createRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { authedLayoutRoute } from './authed.js';
import { listPayrollRuns } from '../api/payrollRuns.js';
import { Alert } from '../components/Alert.js';
import { apiErrorMessage } from '../components/ApiErrorNotice.js';
import { PayrollRunStatusPill } from '../components/PayrollRunStatusPill.js';
import { formatDate, formatMoney } from '../lib/format.js';
import type { PayrollRunResponse } from '@kora/types';

/**
 * /payroll-runs — list of payroll runs for this org, newest first.
 */
export const payrollRunsRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/payroll-runs',
  component: PayrollRunsListPage,
});

function PayrollRunsListPage(): JSX.Element {
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['payroll-runs', 'list'],
    queryFn: () => listPayrollRuns({ limit: 50 }),
  });

  return (
    <section className="p-8 space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-3xl">Payroll runs</h1>
          <p className="text-sm text-gray-400">
            Draft, calculate, approve. Every state change is audit-logged.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void navigate({ to: '/payroll-runs/new' })}
          className="rounded bg-white text-black px-4 py-2 text-sm font-medium hover:bg-gray-200"
        >
          New run
        </button>
      </header>

      {isLoading ? <LoadingState /> : null}
      {isError ? <ErrorState error={error} onRetry={() => void refetch()} /> : null}
      {data && data.runs.length === 0 ? <EmptyState /> : null}
      {data && data.runs.length > 0 ? <RunsTable runs={data.runs} /> : null}
    </section>
  );
}

function RunsTable({ runs }: { runs: PayrollRunResponse[] }): JSX.Element {
  return (
    <div className="rounded border border-gray-900 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gray-950 text-left text-gray-400 uppercase text-xs">
          <tr>
            <th className="px-4 py-3 font-medium">Period</th>
            <th className="px-4 py-3 font-medium">Pay date</th>
            <th className="px-4 py-3 font-medium">Version</th>
            <th className="px-4 py-3 font-medium">Status</th>
            <th className="px-4 py-3 font-medium">Net total</th>
          </tr>
        </thead>
        <tbody>
          {runs.map((r) => (
            <tr key={r.id} className="border-t border-gray-900 hover:bg-gray-950">
              <td className="px-4 py-3">
                <Link
                  to="/payroll-runs/$id"
                  params={{ id: r.id }}
                  className="hover:underline"
                >
                  {formatDate(r.periodStart)} → {formatDate(r.periodEnd)}
                </Link>
              </td>
              <td className="px-4 py-3 text-gray-400">{formatDate(r.payDate)}</td>
              <td className="px-4 py-3 text-gray-400">v{r.version}</td>
              <td className="px-4 py-3">
                <PayrollRunStatusPill status={r.status} />
              </td>
              <td className="px-4 py-3 font-mono text-xs text-gray-300 text-right">
                {r.totals ? formatMoney(r.totals.netTotal, r.totals.currency) : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LoadingState(): JSX.Element {
  return (
    <div className="rounded border border-gray-900 p-8 text-center text-gray-500">
      Loading payroll runs...
    </div>
  );
}

function EmptyState(): JSX.Element {
  return (
    <div className="rounded border border-dashed border-gray-800 p-10 text-center space-y-3">
      <h2 className="font-medium text-white">No payroll runs yet</h2>
      <p className="text-sm text-gray-400">
        Run your first payroll for this org.
      </p>
      <Link
        to="/payroll-runs/new"
        className="inline-block rounded bg-white text-black px-4 py-2 text-sm font-medium"
      >
        New run
      </Link>
    </div>
  );
}

function ErrorState({
  error,
  onRetry,
}: {
  error: unknown;
  onRetry: () => void;
}): JSX.Element {
  return (
    <Alert
      variant="error"
      title="Could not load payroll runs"
      action={
        <button
          type="button"
          onClick={onRetry}
          className="rounded border border-red-800 px-3 py-1 text-xs hover:bg-red-900/40"
        >
          Retry
        </button>
      }
    >
      {apiErrorMessage(error, 'Unknown error')}
    </Alert>
  );
}
